import { Activity, Check, X } from "lucide-react";
import { GateStatusPill } from "./shared";

export default function GateTimeline({ gates = [], selectedGateId, onSelect }) {
    return (
        <div className="border border-hairline bg-jet-elev1" data-testid="gate-timeline-panel">
            <div className="border-b border-hairline px-5 py-3 flex items-center gap-2">
                <Activity className="w-3 h-3 text-signal" />
                <span className="overline">Gate Timeline</span>
                <span className="text-xs font-mono text-snow-muted ml-auto">
                    {gates.filter((g) => g.status === "Passed").length} / {gates.length} passed
                </span>
            </div>

            {gates.length === 0 ? (
                <div className="p-6 text-snow-muted text-xs font-mono">
                    No gates initialized. Assign a control mode to this product.
                </div>
            ) : (
                <ol className="p-5 space-y-0" data-testid="gate-timeline">
                    {gates.map((g, idx) => {
                        const isSelected = selectedGateId === g.id;
                        const isPassed = g.status === "Passed";
                        const isActive = g.status === "In Progress";
                        const isReview = g.status === "In Review";
                        const isFailed = g.status === "Failed";
                        const isHold = g.status === "On Hold";
                        const isLast = idx === gates.length - 1;
                        // Per spec: Snow=Passed, Orange=Active/In Review, Graphite=locked/future
                        const lineColor = isPassed
                            ? "#F5F5F5"
                            : isActive || isReview
                            ? "#FF6A00"
                            : "#2A2A2A";
                        const dotColor = isPassed
                            ? "#F5F5F5"
                            : isActive || isReview
                            ? "#FF6A00"
                            : isFailed
                            ? "#FF3B30"
                            : isHold
                            ? "#FFC107"
                            : "#2A2A2A";
                        return (
                            <li
                                key={g.id}
                                data-testid={`gate-node-${g.gate_id}`}
                                className="relative flex gap-4 pb-4 cursor-pointer"
                                onClick={() => onSelect(g.id)}
                            >
                                {!isLast && (
                                    <div
                                        className="absolute left-[15px] top-8 w-px"
                                        style={{ height: "calc(100% - 8px)", backgroundColor: lineColor }}
                                    />
                                )}
                                <div
                                    className={`relative z-10 w-8 h-8 border-2 flex items-center justify-center text-[10px] font-mono font-bold shrink-0 transition-all ${
                                        isSelected ? "scale-110" : ""
                                    } ${isReview ? "animate-pulse-signal" : ""}`}
                                    style={{
                                        borderColor: dotColor,
                                        color: isPassed || isActive || isReview ? "#111111" : dotColor,
                                        backgroundColor: isPassed || isActive || isReview ? dotColor : "#111111",
                                    }}
                                >
                                    {isPassed ? (
                                        <Check className="w-3.5 h-3.5" />
                                    ) : isFailed ? (
                                        <X className="w-3.5 h-3.5" />
                                    ) : (
                                        `G${String(g.gate_id).padStart(2, "0")}`
                                    )}
                                </div>
                                <div
                                    className={`flex-1 border px-3 py-2 transition-colors ${
                                        isSelected
                                            ? "border-signal bg-signal/5"
                                            : "border-hairline hover:border-snow-dim"
                                    }`}
                                >
                                    <div className="flex items-center justify-between gap-3">
                                        <div className="font-mono text-sm truncate">{g.gate_name}</div>
                                        <GateStatusPill status={g.status} />
                                    </div>
                                    <div className="text-[10px] text-snow-muted mt-1 font-mono uppercase tracking-wider">
                                        {g.deliverable_links?.length || 0} deliverable
                                        {(g.deliverable_links?.length || 0) === 1 ? "" : "s"}
                                    </div>
                                </div>
                            </li>
                        );
                    })}
                </ol>
            )}
        </div>
    );
}
